import { useRelayOwnership } from '@core/realtime/useRelayOwnership';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { getCqgRelay, type RelayConnState } from '../lib/cqgRelay';
import { deriveBadgeDisplay, type CqgBeState } from '../lib/cqgUiState';

/** GET /api/v1/cqg/status response (also the `cqg.state` push payload shape). Canonical copy —
 * import from here instead of re-declaring (the connect dialog used to carry a duplicate). */
export interface CqgStatus {
  state: CqgBeState;
  /** Account ids owned by the LIVE session (same keys as `AccountInfo.name`). Empty when no session. */
  accounts: string[];
  lastError: string | null;
  reason: string | null;
}

const STATES: readonly CqgBeState[] = ['disconnected', 'connecting', 'live', 'error'];

function normalise(state: string | undefined | null): CqgBeState {
  const key = (state ?? 'disconnected').toLowerCase() as CqgBeState;
  return STATES.includes(key) ? key : 'disconnected';
}

/**
 * The CQG connection state as the USER should perceive it — shared by the top-bar
 * `AccountConnectionControl` / Accounts panel and the Open-Positions "CQG connection is down"
 * banner so they can never disagree. One source of
 * truth: the `['cqg','status']` query (60s poll), corrected in real time by `cqg.state` pushes
 * (written into the SAME cache key), combined with THIS TAB's actual browser→CQG socket state —
 * "live" only when the BE session is live AND the socket is really open (a BE `live` over a dead
 * socket after a refresh must read as `connecting`, not a false `live`).
 */
export function useCqgDisplayState(): {
  display: CqgBeState;
  beState: CqgBeState;
  relayConn: RelayConnState;
  /** Account ids owned by the live session — the per-account "is live" join key (Accounts panel). */
  accounts: string[];
  reason: string | null;
  lastError: string | null;
} {
  const api = useApiClient();
  const realtime = useRealtime();
  const queryClient = useQueryClient();
  const [relayConn, setRelayConn] = useState<RelayConnState>('closed');
  const ownership = useRelayOwnership('cqg');

  const status = useQuery({
    queryKey: ['cqg', 'status'],
    queryFn:  () => api.get<CqgStatus>('/api/v1/cqg/status'),
    refetchInterval: 60_000,
  });

  // Fold each cqg.state push into the SHARED ['cqg','status'] cache (same key the poll uses), so
  // every consumer reads one truth and the poll still corrects a stale push.
  useEffect(() => {
    return realtime.subscribe('cqg.state', (payload) => {
      queryClient.setQueryData<CqgStatus>(['cqg', 'status'], (prev) => ({
        state: normalise(payload.state),
        accounts: payload.accounts ?? prev?.accounts ?? [],
        lastError: (payload as { lastError?: string | null }).lastError ?? null,
        reason: payload.reason ?? null,
      }));
    });
  }, [realtime, queryClient]);

  // Track the ACTUAL browser→CQG socket for this tab.
  useEffect(() => {
    const relay = getCqgRelay();
    setRelayConn(relay.getConnectionState());
    return relay.onConnectionChange(() => setRelayConn(relay.getConnectionState()));
  }, []);

  const beState = normalise(status.data?.state);
  return {
    display: deriveBadgeDisplay(beState, relayConn, status.data?.reason ?? null, ownership.mode === 'passive'),
    beState,
    relayConn,
    accounts: status.data?.accounts ?? [],
    reason: status.data?.reason ?? null,
    lastError: status.data?.lastError ?? null,
  };
}
